import cloneDeep from 'lodash/cloneDeep';
import { mapState } from 'vuex';
import WidgetChartOptionsEditor from './chart/widgetChartOptionsEditor.vue';
import WidgetChartDataOptionsEditor from './chart/widgetChartDataOptionsEditor.vue';
import WidgetTwinTableOptionsEditor from './twinTable/widgetTwinTableOptionsEditor.vue';
import WidgetTwinMapOptionsEditor from './twinMap/widgetTwinMapOptionsEditor.vue';
import TwinFilterEditor from './twinFilterEditor.vue';
import VirtualDeviceFilterEditor from './virtualDeviceFilterEditor.vue';
import api from '@/services/apiService/dashboards';
import get from 'lodash/get';
import TwinMapPopupEditor from './twinMap/twinMapPopupEditor.vue';
import TwinMapActionsEditor from './twinMap/twinMapActionsEditor.vue';
import debounce from 'lodash/debounce';
import WidgetMetaEditor from './widgetMetaEditor.vue';
import ChartCustomOptionsEditorCodeEditor from './chart/custom/chartCustomOptionsEditorCodeEditor.vue';
import TwinMapMarkerOptionsEditor from './twinMap/twinMapMarkerOptionsEditor.vue';
export default {
  name: 'WidgetEditDialog',
  components: {
    WidgetCard: () => import('./widgetCard.vue'),
    WidgetChartOptionsEditor,
    WidgetChartDataOptionsEditor,
    WidgetTwinTableOptionsEditor,
    WidgetTwinMapOptionsEditor,
    TwinFilterEditor,
    VirtualDeviceFilterEditor,
    TwinMapPopupEditor,
    TwinMapActionsEditor,
    WidgetMetaEditor,
    ChartCustomOptionsEditorCodeEditor,
    TwinMapMarkerOptionsEditor
  },
  props: {
    value: {
      type: Boolean,
      required: true
    },
    dashboardId: {
      type: String,
      required: true
    },
    widgetId: {
      type: String,
      required: true
    },
    dateRange: {
      type: Object,
      required: true
    },
    customParameters: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },
  data() {
    return {
      tabItem: 'general',
      isSaving: false,
      editedWidget: null,
      dataSet: {},
      isFetchingDataSet: false,
      formValid: false,
      showOptionsPreview: false
    };
  },
  computed: {
    ...mapState('dashboards', ['dashboards']),
    ...mapState('app', ['inpOutlined', 'color', 'inpFilled']),
    dashboard() {
      return this.dashboards.find(dashboard => dashboard.id === this.dashboardId);
    },
    widget() {
      if (!this.dashboard) {
        return null;
      }
      if (this.isCreating) {
        return {
          id: '__new',
          title: '',
          size: 1,
          layoutOrderIdx: this.dashboard.value.widgets.length,
          type: 'empty',
          twinFilter: {},
          virtualDeviceFilter: {}
        };
      }
      return this.dashboard.value.widgets.find(widget => widget.id === this.widgetId);
    },
    isCreating() {
      return this.widgetId === '__new';
    },
    twinFilter: {
      get() {
        return get(this.editedWidget, 'twinFilter', {});
      },
      set(val) {
        const clone = cloneDeep(this.editedWidget);
        clone.twinFilter = val;
        this.editedWidget = clone;
      }
    },
    virtualDeviceFilter: {
      get() {
        return get(this.editedWidget, 'virtualDeviceFilter', {});
      },
      set(val) {
        const clone = cloneDeep(this.editedWidget);
        clone.virtualDeviceFilter = val;
        this.editedWidget = clone;
      }
    }
  },
  watch: {
    widget: {
      handler(newVal, oldVal) {
        if (JSON.stringify(newVal) === JSON.stringify(oldVal)) {
          return;
        }
        this.editedWidget = cloneDeep(newVal);
      },
      deep: true,
      immediate: true
    },
    editedWidget: {
      handler() {
        if (!this.editedWidget) {
          return;
        }
        this.fetchDataSet();
      },
      deep: true,
      immediate: true
    },
    value: {
      handler(isOpen) {
        if (!isOpen) {
          return;
        }
        this._fetchDataSet();
      }
    }
  },
  methods: {
    get,
    fetchDataSet: debounce(function () {
      this._fetchDataSet();
    }, 400),
    async _fetchDataSet() {
      if (!this.value) {
        return;
      }
      try {
        this.isFetchingDataSet = true;
        const result = await api.fetchDataForWidget(this.dashboardId, this.editedWidget, this.dateRange, this.customParameters);
        this.dataSet = {
          data: result,
          success: true
        };
      } catch (e) {
        console.error('error', e);
        this.dataSet = {
          data: null,
          success: false,
          error: e
        };
      } finally {
        this.isFetchingDataSet = false;
      }
    },
    async save() {
      this.$refs.form.validate();
      if (!this.formValid) {
        this.$snack(this.$t('Dashboards.components.widgetEditDialog.snack.error.formInvalid'), 'error');
        return;
      }
      this.isSaving = true;
      let chartOptions;
      if (this.editedWidget.type === 'chart') {
        chartOptions = this.editedWidget.chartOptions;
      }
      let twinTableOptions;
      if (this.editedWidget.type === 'twin-table') {
        twinTableOptions = this.editedWidget.twinTableOptions;
      }
      let twinMapOptions;
      if (this.editedWidget.type === 'twin-map') {
        twinMapOptions = this.editedWidget.twinMapOptions;
      }
      const widgetConfig = {
        ...this.editedWidget,
        chartOptions,
        twinTableOptions,
        twinMapOptions
      };
      let storeAction = 'dashboards/changeWidgetConfig';
      if (this.isCreating) {
        storeAction = 'dashboards/addWidget';
      }
      const {
        success,
        error
      } = await this.$store.dispatch(storeAction, {
        dashboardId: this.dashboardId,
        widgetId: this.isCreating ? undefined : this.widgetId,
        [this.isCreating ? 'widget' : 'config']: widgetConfig
      });
      this.isSaving = false;
      if (!success) {
        console.error(error);
        this.$snack('Could not save widget, see browser console for details', 'error');
        return;
      }
      this.$emit('saved');
    },
    setTwinFilter(val) {
      const clone = cloneDeep(this.editedWidget);
      clone.twinFilter = val;
      this.editedWidget = clone;
    },
    setVirtualDeviceFilter(val) {
      const clone = cloneDeep(this.editedWidget);
      clone.virtualDeviceFilter = val;
      this.editedWidget = clone;
    }
  }
};